import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { BACKEND_URL } from "../../config";

export const salesApi = createApi({
  reducerPath: "salesApi",
  tagTypes: ["RecordingSocial", "RecordingResume"],
  keepUnusedDataFor: 300, // Keep cached data for 5 minutes
  baseQuery: fetchBaseQuery({
    baseUrl: `${BACKEND_URL}/sales`,
    prepareHeaders: (headers, { getState }) => {
      const token = getState().auth.token;
      if (token) {
        // include token in req header
        headers.set("authorization", `Bearer ${token}`);
      }
      return headers;
    },
  }),
  endpoints: (builder) => ({
    getSalesRecording: builder.query({
      query: () => `/portal/get-sales-recordings-video`,
    }),
    getSalesRecordingTags: builder.query({
      query: () => `/portal/get-sales-recordings-tag`,
      keepUnusedDataFor: 1800, // Tags rarely change — cache for 30 minutes
    }),
    // Comments, replies and reactions for one recording (MySQL, thread #1732).
    getRecordingSocial: builder.query({
      query: (recordingId) => `/portal/recording-social/${recordingId}`,
      providesTags: (result, error, recordingId) => [{ type: "RecordingSocial", id: recordingId }],
      // The BFF answers { status, data: { recordingId, reactions, comments } }. The drawer reads
      // data.comments and data.reactions, so unwrap here; without it the drawer shows "No comments yet".
      transformResponse: (res) => res?.data,
    }),
    addRecordingComment: builder.mutation({
      query: ({ recordingId, body, parentId }) => ({
        url: `/portal/recording-social/${recordingId}/comments`,
        method: "POST",
        body: { body, parentId: parentId ?? null },
      }),
      invalidatesTags: (result, error, { recordingId }) => [{ type: "RecordingSocial", id: recordingId }],
    }),
    editRecordingComment: builder.mutation({
      query: ({ commentId, body }) => ({
        url: `/portal/recording-comments/${commentId}`,
        method: "PATCH",
        body: { body },
      }),
      invalidatesTags: (result, error, { recordingId }) => [{ type: "RecordingSocial", id: recordingId }],
    }),
    deleteRecordingComment: builder.mutation({
      query: ({ commentId }) => ({ url: `/portal/recording-comments/${commentId}`, method: "DELETE" }),
      invalidatesTags: (result, error, { recordingId }) => [{ type: "RecordingSocial", id: recordingId }],
    }),
    setRecordingReaction: builder.mutation({
      query: ({ recordingId, commentId, reaction }) => ({
        url: `/portal/recording-reactions`,
        method: "POST",
        body: { recordingId, commentId: commentId || 0, reaction },
      }),
      invalidatesTags: (result, error, { recordingId }) => [{ type: "RecordingSocial", id: recordingId }],
    }),
    getRecordingResume: builder.query({
      query: (recordingId) => `/portal/recording-resume/${recordingId}`,
      providesTags: (result, error, recordingId) => [{ type: "RecordingResume", id: recordingId }],
    }),
    putRecordingResume: builder.mutation({
      query: ({ recordingId, positionSecs }) => ({
        url: `/portal/recording-resume/${recordingId}`,
        method: "PUT",
        body: { positionSecs },
      }),
    }),
  }),
});

export const {
  useGetSalesRecordingQuery,
  useGetSalesRecordingTagsQuery,
  useGetRecordingSocialQuery,
  useAddRecordingCommentMutation,
  useEditRecordingCommentMutation,
  useDeleteRecordingCommentMutation,
  useSetRecordingReactionMutation,
  useGetRecordingResumeQuery,
  usePutRecordingResumeMutation,
} = salesApi;
